import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import emailjs from '@emailjs/browser';
import { trackDemoRequest, identifyUser } from '../utils/analytics';


const DemoPage = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isAgreed, setIsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const form = useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please fill in all required fields');
      return;
    }
    
    if (!isAgreed) {
      setError('Please agree to receive communications');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      // These parameters will be sent to EmailJS
      const result = await emailjs.sendForm(
        'service_scshrvj',      // Replace with your actual Service ID from EmailJS
        'template_imortcu',     // Replace with your actual Template ID from EmailJS
        form.current,
        'p9BinI6yAPNSIHbjh'       // Replace with your actual Public Key from EmailJS
      );
      
      console.log('EmailJS result:', result);

      // Track the successful demo request in PostHog
      const formData = {
        email,
        name
      };
      
      // Identify the user in PostHog
      identifyUser(email, {
        name,
        $initial_referrer: document.referrer
      });
      
      // Track the demo request event
      trackDemoRequest(formData);
      
      setIsSuccess(true);
      setEmail('');
      setName('');
      setIsAgreed(false);
    } catch (err) {
      console.error('Error sending email:', err);
      setError(`Failed to send demo request: ${err.text || err.message}. Please try again or email us directly at info@revelioai.com`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />
      
      <div className="container mx-auto py-16 px-4 pt-32">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold mb-2">Request a Demo</h1>
            <p className="text-gray-400">See how Revelio AI can protect your enterprise AI systems</p>
          </div>

          {isSuccess ? (
            <div className="bg-gray-800 border border-green-500 rounded-lg p-8 text-center">
              <svg className="w-16 h-16 text-green-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h2 className="text-2xl font-bold text-green-400 mb-2">Demo Request Sent!</h2>
              <p className="text-gray-300 mb-6">Thank you for your interest in Revelio AI. Our team will contact you shortly to schedule your demo.</p>
              <Link 
                to="/" 
                className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-6 rounded"
              >
                Return to Home
              </Link>
            </div>
          ) : (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-8">
              <form ref={form} onSubmit={handleSubmit}>
                <div className="mb-6">
                  <label htmlFor="name" className="block mb-2 font-medium">Full Name*</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="user_name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white" 
                    required 
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="email" className="block mb-2 font-medium">Email*</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="user_email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white" 
                    required 
                  />
                </div>
                <div className="mb-6">
                  <label className="flex items-center">
                    <input 
                      type="checkbox" 
                      name="consent"
                      checked={isAgreed}
                      onChange={(e) => setIsAgreed(e.target.checked)}
                      className="mr-2 bg-gray-700 border-gray-600" 
                      required
                    />
                    <span className="text-gray-300">I agree to receive communications from Revelio AI.</span>
                  </label>
                </div>

                <div className="mb-6 text-sm text-gray-400">
                  <p>
                    Revelio AI is committed to protecting and respecting your privacy. By clicking 
                    submit below, you consent to allow Revelio AI to store and process personal 
                    information.
                  </p>
                </div>

                {error && (
                  <div className="mb-6 p-3 bg-red-900/50 text-red-300 rounded border border-red-500">
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-4 rounded disabled:opacity-70 transition-colors"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Request Demo'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default DemoPage;